// ============================================
// APPLICATIONS LIST PAGE
// ============================================

import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  FileText,
  Calendar,
  MoreVertical,
  Eye,
  CheckCircle,
  XCircle,
  MessageSquare,
  Briefcase,
} from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  PageHeader,
  DataTable,
  StatusBadge,
  FilterBar,
  FilterSelect,
  ConfirmDialog,
  EmptyState,
} from '@/components';
import { jobsApi, applicationsApi } from '@/services/api';
import { useDebounce } from '@/hooks';
import { formatRelativeTime, getInitials, cn } from '@/utils';
import { useTranslation } from '@crew-at-sea/i18n';
import type { Column } from '@/components';

export const Route = createFileRoute('/_authenticated/applications/')({
  component: ApplicationsListPage,
});

function ApplicationsListPage() {
  const { t } = useTranslation(['panel', 'common', 'enums']);
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [jobFilter, setJobFilter] = useState('all'); // Default to "all"
  // const { page, limit, goToPage, setLimit } = usePagination(1, 10); // TODO: Implement pagination
  const debouncedSearch = useDebounce(search, 300);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    application: any | null;
    action: string;
  }>({ open: false, application: null, action: '' });

  // Durum seçenekleri - enum'lardan çekilir
  const statusOptions = [
    { value: 'PENDING', label: t('enums:applicationStatus.PENDING') },
    { value: 'REVIEWED', label: t('enums:applicationStatus.REVIEWED') },
    { value: 'SHORTLISTED', label: t('enums:applicationStatus.SHORTLISTED') },
    { value: 'INTERVIEW', label: t('enums:applicationStatus.INTERVIEW') },
    { value: 'OFFERED', label: t('enums:applicationStatus.OFFERED') },
    { value: 'ACCEPTED', label: t('enums:applicationStatus.ACCEPTED') },
    { value: 'REJECTED', label: t('enums:applicationStatus.REJECTED') },
    { value: 'WITHDRAWN', label: t('enums:applicationStatus.WITHDRAWN') },
  ];

  // Fetch jobs for filter dropdown
  const { data: jobsData } = useQuery({
    queryKey: ['jobs', 'me', { limit: 50 }],
    queryFn: () => jobsApi.getMyJobs({ limit: 50 }),
  });

  // Fetch all company applications (when "all" is selected)
  const { data: allApplicationsData, isLoading: allLoading } = useQuery({
    queryKey: ['applications', 'company'],
    queryFn: () => applicationsApi.getCompanyApplications(),
    enabled: jobFilter === 'all',
  });

  // Fetch applications for specific job
  const { data: jobApplicationsData, isLoading: jobLoading } = useQuery({
    queryKey: ['applications', 'job', jobFilter],
    queryFn: () => jobsApi.getApplications(jobFilter),
    enabled: !!jobFilter && jobFilter !== 'all',
  });

  // Combine loading states
  const isLoading = jobFilter === 'all' ? allLoading : jobLoading;

  // Get raw data based on filter
  const rawData = jobFilter === 'all' ? allApplicationsData : jobApplicationsData;

  // Handle backend response wrapper { success: true, data: [...] } or { items: [...] }
  const applicationsData = Array.isArray(rawData) 
    ? rawData 
    : (Array.isArray((rawData as any)?.data) 
        ? (rawData as any).data 
        : (Array.isArray((rawData as any)?.items) 
            ? (rawData as any).items 
            : []));

  // Filter applications locally
  const filteredApplications = applicationsData.filter((app: any) => {
    if (statusFilter && app.status !== statusFilter) return false;
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      const name = `${app.seafarer?.firstName || ''} ${app.seafarer?.lastName || ''}`.toLowerCase();
      if (!name.includes(searchLower)) return false;
    }
    return true;
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      applicationsApi.updateStatus(id, { status: status as any }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applications'] });
      setConfirmDialog({ open: false, application: null, action: '' });
    },
  });

  const handleStatusChange = (applicationId: string, status: string) => {
    updateStatusMutation.mutate({ id: applicationId, status });
  };

  const handleConfirmAction = () => {
    if (!confirmDialog.application || !confirmDialog.action) return;
    updateStatusMutation.mutate({
      id: confirmDialog.application.id,
      status: confirmDialog.action,
    });
  };

  const columns: Column<any>[] = [
    {
      key: 'seafarer',
      header: t('panel:applications.columns.applicant'),
      render: (app) => (
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-ocean-100 text-ocean-700 font-medium">
            {app.seafarer
              ? getInitials(app.seafarer.firstName, app.seafarer.lastName)
              : '??'}
          </div>
          <div>
            <p className="font-medium text-gray-900">
              {app.seafarer
                ? `${app.seafarer.firstName} ${app.seafarer.lastName}`
                : '-'}
            </p>
            <p className="text-xs text-gray-500">
              {app.seafarer?.rank?.name || app.seafarer?.currentRank?.nameTr || '-'}
            </p>
          </div>
        </div>
      ),
    },
    {
      key: 'job',
      header: t('panel:applications.columns.job'),
      render: (app) => (
        <div>
          <div className="flex items-center gap-1">
            <Briefcase className="h-4 w-4 text-gray-400" />
            <Link
              to="/jobs/$slug"
              params={{ slug: app.job?.slug }}
              className="font-medium text-gray-900 hover:text-maritime-600"
            >
              {app.job?.title || '-'}
            </Link>
          </div>
        </div>
      ),
    },
    {
      key: 'matchScore',
      className: 'hidden md:table-cell',
      header: t('panel:applications.columns.matchScore'),
      render: (app) => (
        <div className="flex items-center gap-2">
          <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={cn(
                'h-full rounded-full',
                (app.matchScore || 0) >= 70
                  ? 'bg-green-500'
                  : (app.matchScore || 0) >= 50
                    ? 'bg-amber-500'
                    : 'bg-red-500'
              )}
              style={{ width: `${app.matchScore || 0}%` }}
            />
          </div>
          <span className="text-sm font-medium text-gray-700">
            {app.matchScore || 0}%
          </span>
        </div>
      ),
    },
    {
      key: 'expectedSalary',
      className: 'hidden md:table-cell',
      header: t('panel:applications.columns.expectedSalary'),
      render: (app) => (
        <span className="text-gray-600">
          {app.expectedSalary
            ? `$${app.expectedSalary.toLocaleString('tr-TR')}`
            : '-'}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('panel:applications.columns.status'),
      render: (app) => <StatusBadge status={app.status} dot />,
    },
    {
      key: 'date',
      className: 'hidden md:table-cell',
      header: t('panel:applications.columns.date'),
      render: (app) => (
        <div className="flex items-center gap-1 text-gray-500">
          <Calendar className="h-4 w-4 text-gray-400" />
          <span>{formatRelativeTime(app.createdAt)}</span>
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '50px',
      render: (app) => (
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md">
              <MoreVertical className="w-4 h-4" />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              className="min-w-[180px] bg-white rounded-maritime shadow-lg border border-gray-200 py-1 z-50"
              sideOffset={5}
              align="end"
            >
              <DropdownMenu.Item asChild>
                <Link
                  to="/applications/$id"
                  params={{ id: app.id }}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer outline-none"
                >
                  <Eye className="w-4 h-4" />
                  {t('panel:applications.actions.viewDetails')}
                </Link>
              </DropdownMenu.Item>
              <DropdownMenu.Separator className="my-1 border-t border-gray-200" />
              
              {app.status === 'PENDING' && (
                <>
                  <DropdownMenu.Item
                    className="flex items-center gap-2 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 cursor-pointer outline-none"
                    onSelect={() => handleStatusChange(app.id, 'REVIEWED')}
                  >
                    <Eye className="w-4 h-4" />
                    {t('panel:applications.actions.markReviewed')}
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    className="flex items-center gap-2 px-3 py-2 text-sm text-green-600 hover:bg-green-50 cursor-pointer outline-none"
                    onSelect={() => handleStatusChange(app.id, 'SHORTLISTED')}
                  >
                    <CheckCircle className="w-4 h-4" />
                    {t('panel:applications.actions.shortlist')}
                  </DropdownMenu.Item>
                </>
              )}
              
              {app.status === 'REVIEWED' && (
                <DropdownMenu.Item
                  className="flex items-center gap-2 px-3 py-2 text-sm text-green-600 hover:bg-green-50 cursor-pointer outline-none"
                  onSelect={() => handleStatusChange(app.id, 'SHORTLISTED')}
                >
                  <CheckCircle className="w-4 h-4" />
                  {t('panel:applications.actions.shortlist')}
                </DropdownMenu.Item>
              )}
              
              {app.status === 'SHORTLISTED' && (
                <DropdownMenu.Item
                  className="flex items-center gap-2 px-3 py-2 text-sm text-purple-600 hover:bg-purple-50 cursor-pointer outline-none"
                  onSelect={() => handleStatusChange(app.id, 'INTERVIEW')}
                >
                  <MessageSquare className="w-4 h-4" />
                  {t('panel:applications.actions.interview')}
                </DropdownMenu.Item>
              )}

              {['PENDING', 'REVIEWED', 'SHORTLISTED'].includes(app.status) && (
                <DropdownMenu.Item
                  className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 cursor-pointer outline-none"
                  onSelect={() => setConfirmDialog({ open: true, application: app, action: 'REJECTED' })}
                >
                  <XCircle className="w-4 h-4" />
                  {t('panel:applications.actions.reject')}
                </DropdownMenu.Item>
              )}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      ),
    },
  ];

  // Generate job options for filter
  // Handle nested response: { success, data: { data: [...], meta } } or { data: [...] } or { items: [...] }
  const responseData = (jobsData as any)?.data;
  const jobsList = Array.isArray(responseData?.data) 
    ? responseData.data 
    : (Array.isArray(responseData) 
        ? responseData 
        : (Array.isArray((jobsData as any)?.items) 
            ? (jobsData as any).items 
            : []));
  const jobOptions = [
    { value: 'all', label: t('panel:applications.filter.allJobs') },
    ...jobsList.map((job: any) => ({
      value: job.id,
      label: job.title,
    })),
  ];

  const activeFiltersCount =
    (statusFilter ? 1 : 0) + (search ? 1 : 0) + (jobFilter && jobFilter !== 'all' ? 1 : 0);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('panel:applications.title')}
        description={t('panel:applications.pageDescription')}
      />

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={t('panel:applications.searchPlaceholder')}
        filters={
          <>
            <FilterSelect
              value={jobFilter}
              onChange={setJobFilter}
              options={jobOptions}
              placeholder={t('panel:applications.filter.selectJob')}
            />
            <FilterSelect
              value={statusFilter}
              onChange={setStatusFilter}
              options={statusOptions}
              placeholder={t('panel:applications.filter.allStatuses')}
            />
          </>
        }
        activeFiltersCount={activeFiltersCount}
        onClearFilters={() => {
          setSearch('');
          setStatusFilter('');
        }}
      />

      {/* İlan seçilmediğinde */}
      {!jobFilter ? (
        <EmptyState
          icon={<Briefcase className="w-8 h-8 text-gray-400" />}
          title={t('panel:applications.empty.selectJob')}
          description={t('panel:applications.empty.selectJobDesc')}
        />
      ) : (
        <DataTable
          columns={columns}
          data={filteredApplications}
          keyExtractor={(app) => app.id}
          loading={isLoading}
          emptyMessage={t('panel:applications.empty.noResults')}
          emptyState={{
            icon: <FileText className="w-8 h-8 text-gray-400" />,
            title: t('panel:applications.empty.noApplications'),
            description: t('panel:applications.empty.noApplicationsDesc'),
          }}
        />
      )}

      <ConfirmDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}
        title={t('panel:applications.confirm.rejectTitle')}
        description={t('panel:applications.confirm.rejectDesc', {
          name: `${confirmDialog.application?.seafarer?.firstName} ${confirmDialog.application?.seafarer?.lastName}`
        })}
        variant="danger"
        confirmText={t('panel:applications.actions.reject')}
        onConfirm={handleConfirmAction}
        loading={updateStatusMutation.isPending}
      />
    </div>
  );
}
